import { useState, useEffect, useRef } from 'react';
import { Edit2, Tick, Cross } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { HoverIntentGroup } from '@/components/ui/hover-intent-group';
import { useOutsideSave } from '@/hooks/use-outside-save';
import { isTextSelected } from '@/lib/is-text-selected';

interface EditableTextProps {
    value?: string;
    onSave: (value: string) => void;
    placeholder?: string;
    className?: string;
    type?: string;
    displayValue?: string;
    disabled?: boolean;
    max?: string;
    maxLength?: number;
}

export default function EditableText({
    value,
    onSave,
    placeholder = 'Clique para editar...',
    className = '',
    type = 'text',
    displayValue,
    disabled = false,
    max,
    maxLength,
}: EditableTextProps) {
    const [editValue, setEditValue] = useState(value || '');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const editorContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setEditValue(value || '');
    }, [value]);

    useOutsideSave({
        isActive: isEditing,
        disabled,
        containerRef: editorContainerRef,
        currentValue: value || '',
        editedValue: editValue,
        onSave,
        onClose: () => setIsEditing(false),
    });

    const handleSave = () => {
        onSave(editValue);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value || '');
        setIsEditing(false);
    };

    if (isEditing && !disabled) {
        return (
            <div ref={editorContainerRef} className="flex items-center gap-2">
                <input
                    type={type}
                    value={editValue}
                    max={max}
                    maxLength={maxLength}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 rounded border border-blue-500 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave();
                        if (e.key === 'Escape') handleCancel();
                    }}
                />
                <Button
                    type="button"
                    size="iconXs"
                    onClick={handleSave}
                    aria-label="Salvar"
                    className="[&_svg]:size-3"
                >
                    <Tick />
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    size="iconXs"
                    onClick={handleCancel}
                    aria-label="Cancelar"
                    className="bg-slate-200 text-slate-700 hover:bg-slate-300 [&_svg]:size-3"
                >
                    <Cross />
                </Button>
            </div>
        );
    }

    const displayText = displayValue !== undefined ? displayValue : value;

    return (
        <HoverIntentGroup
            hoverIntent={{ disabled }}
            onClick={(e) => {
                if (disabled) return;
                if (isTextSelected(e.currentTarget)) return;
                setIsEditing(true);
            }}
            className={`group/field relative ${
                disabled ? '' : 'cursor-text hover:bg-blue-50'
            } -mx-2 -my-1 rounded px-2 py-1 pr-6 transition-colors ${className}`}
        >
            {displayText || <span className="italic text-slate-400">{placeholder}</span>}
            {!disabled && (
                <div className="absolute -right-1 top-1/2 -translate-y-1/2 rounded-md border border-slate-200 bg-white p-1 opacity-0 shadow-sm transition-all duration-200 group-data-[hover-intent=on]/field:opacity-100">
                    <Edit2 className="size-3.5 text-slate-500" />
                </div>
            )}
        </HoverIntentGroup>
    );
}
